import { ExperiencesAvailabilityRangeService, RTKUtils, type ExperiencesRangeParams } from '@sevenrooms/api'
import type { ExperienceAvailabilitySingleDay } from '@sevenrooms/domain'
import { baseReservationWidgetApi } from './BaseReservationWidgetApi'

const experienceRangeApiTags = ['ExperienceRange']

interface VenueExperiences {
  isFetching: boolean
  experiences: ExperienceAvailabilitySingleDay[] | undefined
  error: string | undefined
}

export interface VenuesExperiences {
  [id: string]: VenueExperiences
}

type GetMultiVenueExperiencesAvailabilitySingleDayParams = Omit<ExperiencesRangeParams, 'numDays' | 'venue'> & { venueIds: string[] }

export const experiencesAvailabilityRangeApi = baseReservationWidgetApi
  .enhanceEndpoints({ addTagTypes: experienceRangeApiTags })
  .injectEndpoints({
    endpoints: builder => ({
      getPrivateDiningExperiencesSingleDay: builder.query({
        providesTags: experienceRangeApiTags,
        queryFn: RTKUtils.rtkQuery(ExperiencesAvailabilityRangeService.getPrivateDiningExperiencesSingleDay),
      }),
      getExperiencesAvailabilitySingleDay: builder.query({
        providesTags: experienceRangeApiTags,
        queryFn: RTKUtils.rtkQuery(ExperiencesAvailabilityRangeService.getExperiencesAvailabilitySingleDay),
      }),
      getMultiVenueExperiencesAvailabilitySingleDay: builder.query<VenuesExperiences, GetMultiVenueExperiencesAvailabilitySingleDayParams>({
        providesTags: experienceRangeApiTags,
        async queryFn(_arg, _api, _extraOptions) {
          const { venueIds } = _arg
          const response = Object.fromEntries(venueIds.map(id => [id, { experiences: undefined, error: undefined, isFetching: true }]))

          // this is necessary for initialization cache for getMultiVenueExperiencesAvailabilitySingleDay query
          _api.dispatch(
            experiencesAvailabilityRangeApi.util.updateQueryData('getMultiVenueExperiencesAvailabilitySingleDay', _arg, data => {
              if (!data) {
                return response
              }
              return { ...data }
            })
          )
          Promise.all(
            venueIds.map(async id => {
              const venueExperiences: VenueExperiences = { experiences: undefined, error: undefined, isFetching: true }
              try {
                venueExperiences.experiences = await ExperiencesAvailabilityRangeService.getExperiencesAvailabilitySingleDay({
                  ..._arg,
                  venue: id,
                })
              } catch (e) {
                venueExperiences.error = e as string
              } finally {
                venueExperiences.isFetching = false
              }
              _api.dispatch(
                experiencesAvailabilityRangeApi.util.updateQueryData('getMultiVenueExperiencesAvailabilitySingleDay', _arg, data => ({
                  ...data,
                  [id]: venueExperiences,
                }))
              )
            })
          )
          return { data: response }
        },
      }),
    }),
  })

export const { useGetExperiencesAvailabilitySingleDayQuery, useGetMultiVenueExperiencesAvailabilitySingleDayQuery } =
  experiencesAvailabilityRangeApi
